/**
 * AIghtBet Frontend — PostHog instrumentation
 * ===========================================
 * Companion to `sentry.ts`. Wires PostHog for product analytics +
 * pageview/click autocapture. Distinct ID matches the Supabase auth user
 * id (`sub` claim) so events join cleanly with backend + edge surfaces.
 *
 * Project: same PostHog project the edge functions use (id 401746).
 * All events get `surface: frontend` so they're filterable apart from
 * backend (`surface: backend`) and edge (`function_slug: <slug>`).
 *
 * KAN-128: PostHog init is gated on cookie consent. Until the user
 * accepts via the banner, `initPostHog()` is a no-op and
 * `captureEvent` / `identifyUser` silently drop. When consent flips to
 * accepted (banner Accept / Settings save with analytics on), the
 * `consent.ts` listener calls `initPostHog()` again and PostHog comes
 * up live without a page reload.
 *
 * Required env (build-time):
 *   VITE_POSTHOG_KEY   — phc_* public project key
 *   VITE_POSTHOG_HOST  — defaults to https://us.i.posthog.com
 */

import posthog, { PostHog } from 'posthog-js';
import { hasAnalyticsConsent, onConsentChange } from './lib/consent';

let _initialized = false;
let _consentListenerAttached = false;

/**
 * Initialize the PostHog browser SDK. Idempotent. No-ops gracefully when
 * `VITE_POSTHOG_KEY` is unset (local dev / preview without secrets) or
 * when the user has not granted analytics consent (KAN-128).
 *
 * Safe to call repeatedly — once consent flips to accepted the deferred
 * init runs on the next call.
 */
export function initPostHog(): PostHog | null {
  if (_initialized) return posthog;

  // KAN-128: never init PostHog before the user consents. Attach a
  // listener once so a later Accept (post-first-paint) triggers init
  // without a reload.
  if (!_consentListenerAttached) {
    _consentListenerAttached = true;
    onConsentChange(() => {
      if (hasAnalyticsConsent() && !_initialized) {
        initPostHog();
      } else if (!hasAnalyticsConsent() && _initialized) {
        // User revoked — stop sending events. Keep state so a later
        // re-accept just calls opt_in_capturing().
        try {
          posthog.opt_out_capturing();
        } catch {
          /* no-op */
        }
      }
    });
  }

  if (!hasAnalyticsConsent()) {
    return null;
  }

  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!key) {
    console.info('[posthog] VITE_POSTHOG_KEY not set — PostHog disabled');
    return null;
  }

  try {
    posthog.init(key, {
      api_host:
        (import.meta.env.VITE_POSTHOG_HOST as string) ||
        'https://us.i.posthog.com',

      // Pageview + click + form autocapture — useful baseline; tune later
      // if any specific surfaces need to opt out.
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,

      // KAN-107: enable PostHog's $exception autocapture. Previously every
      // unhandled error landed in Sentry but PostHog stayed at 0
      // `$exception` events for 7 days, breaking the funnel-error
      // correlation. PostHog's exception capture wraps `window.onerror`
      // and `unhandledrejection` to fire a `$exception` event with the
      // stack and the active person's identity attached.
      capture_exceptions: true,

      // Default to NO session recording. Replays are expensive and the
      // user hasn't opted in; flip on case-by-case via feature flags
      // once we know what we want to capture.
      disable_session_recording: true,

      // Privacy: mask all text + inputs in any future replay.
      session_recording: {
        maskAllInputs: true,
      },

      // Tag every event with the surface so it's filterable alongside
      // backend events (`surface: backend`) and edge function events
      // (which carry `function_slug` instead).
      loaded: (ph) => {
        ph.register({ surface: 'frontend' });
      },

      // We identify users explicitly via `identifyUser()` after auth.
      // Don't try to fingerprint anonymous users in any meaningful way.
      bootstrap: { distinctID: undefined },

      // Lower noise from PostHog's own debug output.
      debug: import.meta.env.DEV,
    });

    _initialized = true;
    console.info('[posthog] initialized');
    return posthog;
  } catch (e) {
    console.error('[posthog] init failed:', e);
    return null;
  }
}

/**
 * Identify the current user. Called from SupabaseAuthContext when the
 * session is established. `userId` should be the Supabase auth user id
 * (`user.id` on the Supabase user object) so it matches the edge-function
 * + backend `distinct_id`.
 *
 * `properties` is optional — keep it small + non-PII (tier, role, etc.).
 * NEVER include email, phone, payment IDs, etc.
 */
export function identifyUser(
  userId: string | null | undefined,
  properties?: Record<string, unknown>
): void {
  if (!_initialized) return;
  try {
    if (!userId) {
      posthog.reset();
      return;
    }
    posthog.identify(userId, properties ?? {});
  } catch {
    /* no-op */
  }
}

/**
 * Capture a custom event. Use sparingly for meaningful product moments
 * (subscription_upgraded, prediction_viewed, bet_placed). Autocapture
 * handles routine clicks/views.
 */
export function captureEvent(event: string, properties?: Record<string, unknown>): void {
  if (!_initialized) return;
  try {
    posthog.capture(event, properties ?? {});
  } catch {
    /* no-op */
  }
}

/**
 * Reset the PostHog session. Call on logout to make sure subsequent
 * anonymous events aren't tied to the previous user's distinct_id.
 */
export function resetPostHog(): void {
  if (!_initialized) return;
  try {
    posthog.reset();
  } catch {
    /* no-op */
  }
}

export default initPostHog;
